/**
 * History Component
 * Displays task history and activity timeline
 * Shows user actions and changes over time
 */
import React from 'react';
import {
  Avatar,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { Assignment } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../../shared/colors';
import { REGEX } from '../../shared/regex';
import { HISTORY_FIELDS, HISTORY_ACTION_TYPES } from '../../shared/appConstants';
import { formatCommentTimestamp } from '../../shared/commentUtils';
import { formatDateWithOptionalTime, hasEffectiveDueTime } from '../../shared/timeUtils';

const History = ({ ticketHistory, ticketHistoryLoading }) => {
  const { t } = useTranslation();

  const formatValue = (field, value, change = {}, side = 'new') => {
    if (value === undefined || value === null || value === '') return t('common.notAvailable');
    if (typeof value === 'string') {
      const decoded = value.replace(REGEX.htmlEntities, (m) => ({ '&lt;': '<', '&gt;': '>', '&amp;': '&', '&nbsp;': ' ' }[m]));
      const stripped = decoded.replace(REGEX.htmlTags, '').trim();
      if (stripped === '') return t('common.notAvailable');
    }

    if (field === HISTORY_FIELDS.ESTIMATE_TIME && typeof value === 'number') {
      const h = Math.floor(value / 60);
      const m = value % 60;
      return `${h > 0 ? h + 'h ' : ''}${m}m`;
    }

    if (field === HISTORY_FIELDS.DUE_DATE && typeof value === 'string' && value.includes('T')) {
      const explicitHasDueTime = side === 'old' ? change.oldHasDueTime : change.newHasDueTime;
      // Older history rows do not have has_due_time metadata, so infer from the stored timestamp.
      const hasDueTime = explicitHasDueTime !== undefined
        ? Boolean(explicitHasDueTime)
        : hasEffectiveDueTime(false, value);
      return formatDateWithOptionalTime(value, hasDueTime, { fallback: t('common.notAvailable') });
    }

    if (field === HISTORY_FIELDS.START_DATE && typeof value === 'string' && value.includes('T')) {
      // Start dates do not have a separate has_time flag, so infer display-time from the timestamp itself.
      return formatDateWithOptionalTime(value, hasEffectiveDueTime(false, value), { fallback: t('common.notAvailable') });
    }

    return value;
  };

  /**
   * Generates a translated history message based on record data
   * @param {Object} item History record
   * @returns {string} Translated message
   */
  const renderHistoryMessage = (item) => {
    if (!item) return '';

    // Try to translate based on action_type
    const actionKey = `history.actions.${item?.action_type}`;
    const options = { user: item?.user_name };
    
    // Extract variables from the fallback message if needed, or handle per type
    if (item?.action_type === HISTORY_ACTION_TYPES.ASSIGNEE_ADDED || item?.action_type === HISTORY_ACTION_TYPES.ASSIGNEE_REMOVED) {
        const namesMatch = item?.message?.match(REGEX.historyActionNames);
        options.names = namesMatch ? namesMatch[1] : '';
    }

    if (item?.action_type === HISTORY_ACTION_TYPES.BULK_UPDATED) {
      return item?.message || '';
    }
    
    const translated = t(actionKey, { ...options, defaultValue: item?.message });
    return translated;
  };

  // Decode HTML entities and strip HTML tags from a string.
  // Two passes handle double-encoded content (e.g. &lt;p&gt; stored as entities).
  const decodeHtmlContent = (htmlString) => {
    if (!htmlString) return '';
    const parseHtml = (str) => {
      if (typeof DOMParser === 'undefined') {
        // Fallback for non-browser environments
        return str
          .replace(REGEX.htmlEntities, (m) => ({ '&lt;': '<', '&gt;': '>', '&amp;': '&', '&nbsp;': ' ' }[m]))
          .replace(REGEX.htmlTags, '');
      }
      const doc = new DOMParser().parseFromString(str, 'text/html');
      return doc.body.textContent || doc.body.innerText || '';
    };
    const firstPass = parseHtml(htmlString);
    return (htmlString.includes('&lt;') ? parseHtml(firstPass) : firstPass).trim();
  };

  const renderDetailedChanges = (item) => (
    <Box sx={{ minWidth: 0, width: '100%' }}>
      <Typography variant="body2" sx={{ color: COLORS.text.primary, mb: 1 }}>
        {t('history.updatedFollowingFields', { user: item?.user_name })}
      </Typography>
      <TableContainer sx={{ border: `1px solid ${COLORS.gray[200]}`, borderRadius: 1, overflowX: 'auto' }}>
        <Table size="small" aria-label={t('history.changeTableLabel')} sx={{ tableLayout: 'fixed', minWidth: 480 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: COLORS.gray[50] }}>
              <TableCell sx={{ width: '24%', py: 1, px: 1.5, fontWeight: 600, color: COLORS.text.primary }}>
                {t('history.table.field')}
              </TableCell>
              <TableCell sx={{ width: '38%', py: 1, px: 1.5, fontWeight: 600, color: COLORS.text.primary }}>
                {t('history.table.previousValue')}
              </TableCell>
              <TableCell sx={{ width: '38%', py: 1, px: 1.5, fontWeight: 600, color: COLORS.text.primary }}>
                {t('history.table.newValue')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
        {item.detailedChanges.map((change, index) => {
          const fieldName = t(`history.fields.${change?.field}`, { defaultValue: change?.field });
          const key = `${change?.field || 'change'}-${index}`;

          if (change?.message) {
            return (
              <TableRow key={key}>
                <TableCell colSpan={3} sx={{ py: 1, px: 1.5, color: COLORS.text.secondary, overflowWrap: 'anywhere' }}>
                  {decodeHtmlContent(change.message)}
                </TableCell>
              </TableRow>
            );
          }

          if (change?.field === HISTORY_FIELDS.SUBTASK || change?.field === HISTORY_FIELDS.SUBTASK_REMOVED) {
            const translationKey = change.field === HISTORY_FIELDS.SUBTASK ? 'history.addedSubtask' : 'history.removedSubtask';
            return (
              <TableRow key={key}>
                <TableCell colSpan={3} sx={{ py: 1, px: 1.5, color: COLORS.text.secondary, overflowWrap: 'anywhere' }}>
                  {t(translationKey, { user: item?.user_name, subtask: change?.newValue })}
                </TableCell>
              </TableRow>
            );
          }

          const oldValue = decodeHtmlContent(String(formatValue(change?.field, change?.oldValue, change, 'old')));
          const newValue = decodeHtmlContent(String(formatValue(change?.field, change?.newValue, change, 'new')));

          return (
            <TableRow key={key}>
              <TableCell sx={{ py: 1, px: 1.5, color: COLORS.text.primary, fontWeight: 600, textTransform: 'capitalize', verticalAlign: 'top' }}>
                {fieldName}
              </TableCell>
              <TableCell sx={{ py: 1, px: 1.5, color: COLORS.text.secondary, overflowWrap: 'anywhere', verticalAlign: 'top' }}>
                {oldValue}
              </TableCell>
              <TableCell sx={{ py: 1, px: 1.5, color: COLORS.text.secondary, overflowWrap: 'anywhere', verticalAlign: 'top' }}>
                {newValue}
              </TableCell>
            </TableRow>
          );
        })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Box>
      <Typography variant="h6" fontWeight="600" sx={{ mb: 3, color: COLORS.text.primary }}>
        {t('history.title')}
      </Typography>
      
      <Box>
        {ticketHistoryLoading ? (
          <Typography>{t('history.loadingHistory')}</Typography>
        ) : ticketHistory?.length > 0 ? (
          ticketHistory.filter(item => item !== null && item !== undefined).map((item) => (
          <Box key={item.id} sx={{ mb: 3, pb: 2, borderBottom: `1px solid ${COLORS.gray[100]}` }}>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Avatar sx={{ 
                width: 32, 
                height: 32, 
                fontSize: 12, 
                bgcolor: COLORS.primary,
                fontWeight: 600
              }}>
                {item?.user_name?.charAt(0) || 'U'}
              </Avatar>
              
              <Box sx={{ flex: 1 }}>
                <Box display="flex" alignItems="flex-start" gap={1} mb={0.5}>
                  <Assignment sx={{ fontSize: 16, color: COLORS.gray[500], mt: 0.1 }} />
                  {item?.action_type === HISTORY_ACTION_TYPES.UPDATED && item?.detailedChanges?.length > 0
                    ? renderDetailedChanges(item)
                    : (
                      <Typography variant="body2" sx={{ color: COLORS.text.primary }}>
                        {decodeHtmlContent(renderHistoryMessage(item))}
                      </Typography>
                    )}
                </Box>
                
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ color: COLORS.gray[500], mt: 2.5 }}
                >
                  {item?.created_at ? formatCommentTimestamp(item.created_at) : ''}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))
        ) : (
          <Typography variant="body2" sx={{ color: COLORS.text.secondary, fontStyle: 'italic', textAlign: 'center', py: 2 }}>
            {t('history.noHistoryFound')}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default History;